/* Opal In Sky — small progressive enhancements for band sections.
   Everything here is optional: content is fully usable without JS. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Scroll reveal ---------- */
function initReveal(root = document) {
  const items = root.querySelectorAll('[data-ois-reveal]:not(.is-in)');
  if (!items.length) return;
  if (reduceMotion || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );
  items.forEach((el) => io.observe(el));
}

/* ---------- Marquee pause (WCAG 2.2.2) ---------- */
document.addEventListener('click', (event) => {
  const toggle = event.target instanceof Element && event.target.closest('[data-ois-marquee-toggle]');
  if (!toggle) return;
  const marquee = toggle.closest('marquee-component');
  const paused = toggle.getAttribute('aria-pressed') !== 'true';
  toggle.setAttribute('aria-pressed', String(paused));
  marquee?.toggleAttribute('data-paused', paused);
});

/* ---------- Tour dates: hide past shows client-side (page cache safety net) ---------- */
function todayKey() {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

function initTour(root = document) {
  root.querySelectorAll('[data-ois-tour][data-hide-past="true"]').forEach((list) => {
    const today = todayKey();
    const limit = parseInt(list.dataset.limit || '0', 10);
    let visible = 0;
    list.querySelectorAll('[data-show-key]').forEach((row) => {
      const key = parseInt(row.dataset.showKey, 10);
      const past = key && key < today;
      const overLimit = limit > 0 && !past && visible >= limit;
      row.hidden = past || overLimit;
      if (!row.hidden) visible += 1;
    });
    list.querySelectorAll('[data-month-label]').forEach((label) => {
      label.hidden = !list.querySelector(`[data-month="${label.dataset.monthLabel}"]:not([hidden])`);
    });
    const empty = list.parentElement?.querySelector('[data-ois-tour-empty]');
    if (empty) empty.hidden = visible > 0;
  });
}

/* ---------- Video facade ----------
   Default: opens a lightbox <dialog> with the player. With [data-inline], the thumbnail is
   swapped for the player in place (used for the featured video). */
function embedSrc(provider, id) {
  return provider === 'vimeo'
    ? `https://player.vimeo.com/video/${id}?autoplay=1`
    : `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;
}

function makeIframe(provider, id, title) {
  const iframe = document.createElement('iframe');
  iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
  iframe.allowFullscreen = true;
  iframe.title = title || 'Video';
  iframe.src = embedSrc(provider, id);
  return iframe;
}

let lightbox;
function getLightbox() {
  if (lightbox) return lightbox;
  lightbox = document.createElement('dialog');
  lightbox.className = 'ois-lightbox';
  lightbox.setAttribute('aria-label', 'Video player');
  lightbox.innerHTML =
    '<div class="ois-lightbox__frame"><div class="ois-lightbox__player"></div>' +
    '<p class="ois-lightbox__title"></p></div>' +
    '<button type="button" class="ois-lightbox__close" aria-label="Close video">' +
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2"/></svg></button>';
  const close = () => lightbox.close();
  lightbox.querySelector('.ois-lightbox__close').addEventListener('click', close);
  // Click on the backdrop (outside the frame) closes
  lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox) close();
  });
  lightbox.addEventListener('close', () => {
    lightbox.querySelector('.ois-lightbox__player').replaceChildren(); // stops playback
    document.documentElement.classList.remove('ois-lightbox-open');
    lightbox.opener?.focus();
  });
  document.body.append(lightbox);
  return lightbox;
}

class OisVideo extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.onClick = () => this.play();
    this.button?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  play() {
    const { provider, videoId, title } = this.dataset;
    if (!videoId) return;
    if (this.hasAttribute('data-inline')) {
      const iframe = makeIframe(provider, videoId, title);
      this.replaceChildren(iframe);
      iframe.focus();
      return;
    }
    const box = getLightbox();
    box.querySelector('.ois-lightbox__player').replaceChildren(makeIframe(provider, videoId, title));
    box.querySelector('.ois-lightbox__title').textContent = title || '';
    box.opener = this.button;
    document.documentElement.classList.add('ois-lightbox-open');
    box.showModal();
    box.querySelector('.ois-lightbox__close').focus();
  }
}
if (!customElements.get('ois-video')) customElements.define('ois-video', OisVideo);

/* ---------- Music player facade ----------
   Spotify / Apple Music / Bandcamp players set third-party cookies, so the iframe is only
   created after the visitor clicks. Nothing is requested from the provider before then. */
class OisEmbed extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.onClick = () => this.load();
    this.button?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  load() {
    const { src, title, height } = this.dataset;
    if (!src) return;
    const iframe = document.createElement('iframe');
    iframe.allow = 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture';
    iframe.title = title || 'Music player';
    if (height) iframe.height = height;
    iframe.src = src;
    this.replaceChildren(iframe);
    iframe.focus();
  }
}
if (!customElements.get('ois-embed')) customElements.define('ois-embed', OisEmbed);

/* ---------- Privacy choices ----------
   Opens the cookie preferences so visitors can change or withdraw consent. When the banner isn't loaded on this visit, the link's href (privacy policy)
   is followed instead. */
document.addEventListener('click', (event) => {
  const link = /** @type {Element} */ (event.target).closest?.('[data-ois-privacy-choices]');
  if (!link) return;
  const win = /** @type {any} */ (window);
  // The band cookie bar (assets/ois-consent.js) when enabled, otherwise Shopify's own banner.
  const open = win.oisConsent?.showPreferences ?? win.privacyBanner?.showPreferences?.bind(win.privacyBanner);
  if (typeof open !== 'function') return;
  event.preventDefault();
  open();
});

/* ---------- FAQ: live search + expand/collapse ---------- */
class OisFaq extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-faq-search]');
    this.items = [...this.querySelectorAll('[data-faq-item]')];
    this.groups = [...this.querySelectorAll('[data-faq-group]')];
    this.empty = this.querySelector('[data-faq-empty]');
    this.input?.addEventListener('input', () => this.filter());
    this.querySelector('[data-faq-toggle-all]')?.addEventListener('click', (e) => this.toggleAll(e.currentTarget));
    if (location.hash) {
      const target = this.querySelector(location.hash);
      if (target?.tagName === 'DETAILS') target.open = true;
    }
  }

  filter() {
    const q = this.input.value.trim().toLowerCase();
    let shown = 0;
    for (const item of this.items) {
      const match = !q || item.textContent.toLowerCase().includes(q);
      item.hidden = !match;
      if (match) shown += 1;
      if (q && match) item.open = true;
    }
    for (const group of this.groups) {
      group.hidden = !group.querySelector('[data-faq-item]:not([hidden])');
    }
    if (this.empty) this.empty.hidden = shown > 0;
  }

  toggleAll(button) {
    const open = button.getAttribute('aria-pressed') !== 'true';
    this.items.forEach((d) => {
      d.removeAttribute('name');
      d.open = open;
    });
    button.setAttribute('aria-pressed', String(open));
    button.textContent = open ? button.dataset.labelCollapse : button.dataset.labelExpand;
  }
}
if (!customElements.get('ois-faq')) customElements.define('ois-faq', OisFaq);

/* ---------- Countdown (release / tour announcements) ---------- */
class OisCountdown extends HTMLElement {
  connectedCallback() {
    this.target = Date.parse(this.dataset.target);
    if (Number.isNaN(this.target)) return;
    this.parts = {
      d: this.querySelector('[data-d]'),
      h: this.querySelector('[data-h]'),
      m: this.querySelector('[data-m]'),
      s: this.querySelector('[data-s]'),
    };
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const diff = Math.max(0, this.target - Date.now());
    if (diff === 0) {
      clearInterval(this.timer);
      this.classList.add('is-done');
    }
    const pad = (n) => String(n).padStart(2, '0');
    const d = Math.floor(diff / 864e5);
    const h = Math.floor((diff % 864e5) / 36e5);
    const m = Math.floor((diff % 36e5) / 6e4);
    const s = Math.floor((diff % 6e4) / 1e3);
    if (this.parts.d) this.parts.d.textContent = pad(d);
    if (this.parts.h) this.parts.h.textContent = pad(h);
    if (this.parts.m) this.parts.m.textContent = pad(m);
    if (this.parts.s) this.parts.s.textContent = pad(s);
  }
}
if (!customElements.get('ois-countdown')) customElements.define('ois-countdown', OisCountdown);

/* ---------- Scroll regions: keyboard-focusable only while they overflow (WCAG 2.1.1) ---------- */
const scrollRegionObserver =
  'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
        for (const { target } of entries) {
          if (target.scrollWidth > target.clientWidth + 1) target.setAttribute('tabindex', '0');
          else target.removeAttribute('tabindex');
        }
      })
    : null;

function initScrollRegions(root = document) {
  root.querySelectorAll('[data-ois-scroll-region]').forEach((el) => scrollRegionObserver?.observe(el));
}

/* ---------- New-window links announce themselves (WCAG 3.2.5) ---------- */
const NEW_TAB = 'opens in a new tab';

function initNewTabLinks(root = document) {
  root.querySelectorAll('a[target="_blank"]:not([data-ois-new-tab])').forEach((a) => {
    a.dataset.oisNewTab = '';
    const label = a.getAttribute('aria-label');
    if (label) {
      if (!label.toLowerCase().includes('new tab')) a.setAttribute('aria-label', `${label} (${NEW_TAB})`);
      return;
    }
    if (a.textContent.toLowerCase().includes('new tab')) return;
    const hint = document.createElement('span');
    hint.className = 'visually-hidden';
    hint.textContent = ` (${NEW_TAB})`;
    a.append(hint);
  });
}

/* ---------- Boot ---------- */
function boot(root) {
  initNewTabLinks(root);
  initTour(root);
  initReveal(root);
  initScrollRegions(root);
}

boot(document);

/* Re-run when sections are re-rendered in the theme editor */
document.addEventListener('shopify:section:load', (e) => boot(e.target));
